import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Switch } from "@/components/ui/switch";

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/whatsapp").then((r) => {
    setCfg(r.data.data);
    setPhoneId(r.data.data.phone_number_id || "");
    setEnabled(r.data.data.enabled);
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { phone_number_id: phoneId, enabled };
      if (token) payload.access_token = token;
      await api.put("/admin/whatsapp", payload);
      setToken("");
      toast.success("WhatsApp settings saved");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try { await api.post("/admin/whatsapp/test", { to: testTo }); toast.success("Test message queued"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure the WhatsApp Cloud API used to notify owners of finder messages.</p>
      </div>

      <div className="rounded-2xl border border-border bg-background p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#25D366]/15 text-[#25D366] grid place-items-center"><Icon name="MessageCircle" className="w-5 h-5" /></div>
          <div>
            <p className="font-display font-bold text-lg">WhatsApp Cloud API</p>
            <p className="text-sm text-muted-foreground">Meta Graph API · finder alerts include a Google Maps location link.</p>
          </div>
        </div>

        {cfg && (
          <div className={`text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-2 ${cfg.token_configured ? "bg-[#EAF9EF] text-status-found" : "bg-secondary text-muted-foreground"}`} data-testid="whatsapp-status">
            <Icon name={cfg.token_configured ? "CircleCheck" : "CircleAlert"} className="w-4 h-4" />
            {cfg.token_configured ? "Token configured" : "Not configured"}
          </div>
        )}

        <label className="block">
          <span className="text-sm font-semibold">Phone number ID</span>
          <input data-testid="wa-phone-id" value={phoneId} onChange={(e) => setPhoneId(e.target.value)} placeholder="e.g. 123456789012345" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Access token {cfg?.token_configured && <span className="text-muted-foreground font-normal">(leave blank to keep current)</span>}</span>
          <input data-testid="wa-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Meta system-user token" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
        </label>
        <div className="flex items-center justify-between rounded-xl border border-border p-3.5">
          <span className="font-semibold text-sm">Send WhatsApp on new finder message</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="wa-enabled" />
        </div>
        <button data-testid="wa-save" onClick={save} disabled={busy} className="tap h-12 px-8 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60">Save settings</button>
      </div>

      <div className="rounded-2xl border border-border bg-background p-6 space-y-3">
        <p className="font-display font-bold">Send a test message</p>
        <div className="flex gap-3">
          <input data-testid="wa-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+14155550123" className="flex-1 h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
          <button data-testid="wa-test-send" onClick={sendTest} className="tap h-12 px-6 rounded-full border border-border font-semibold">Send test</button>
        </div>
        <p className="text-xs text-muted-foreground">Recipient must be in your Meta allowed list (E.164 format).</p>
      </div>
    </div>
  );
}
